/* eslint-disable @typescript-eslint/no-empty-function */
import { Roles } from 'ciam-commons';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getUserDetails } from 'src/services/apiService';
import {
  clearCookies,
  clearStorage,
  getAccountInfo,
  logoutRedirect,
  showLoginScreen,
  waitForGigya,
} from 'src/services/gigya/gigya-service';
import { decodeHashToParams } from 'src/utils/decodeHashToParams';
import { toDateString } from 'src/utils/transformers';
import envVariables from '../env';
import { AccountInfo, AuthStatus, Identities, User } from '../types';

interface CurrentOrganisation {
  bpid: string;
  name: string;
  legacyNumber?: string;
}
interface IUserContext {
  user: User | null;
  status: AuthStatus;
  login: () => void;
  logout: () => void;
  update: () => void;
  isDelegate: boolean;
  getCurrentOrg: () => CurrentOrganisation;
}

const UserContext = createContext<IUserContext>({
  user: null,
  status: AuthStatus.Loading,
  login: () => {
    throw new Error('<UserProvider> missing in a component tree');
  },
  logout: () => {
    throw new Error('<UserProvider> missing in a component tree');
  },
  update: () => {
    throw new Error('<UserProvider> missing in a component tree');
  },
  isDelegate: false,
  getCurrentOrg: () => ({
    bpid: null,
    name: '',
    legacyNumber: '',
  }),
});

const useAuth = (): IUserContext => useContext(UserContext);

const mapSocialProviders = (socialProviders: string): string[] => {
  return socialProviders.split(',').map((provider) => {
    const formatedProvider = provider.toLowerCase().trim();
    if (formatedProvider === 'googleplus') {
      return 'google';
    }
    if (formatedProvider === 'messenger') {
      return 'microsoft';
    }
    return formatedProvider;
  });
};

const mapIdentityProviders = (identities: Identities[]): Identities[] => {
  return identities.map((identity) => {
    const formatedProvider = identity.provider.toLowerCase().trim();
    if (formatedProvider === 'googleplus') {
      identity.provider = 'google';
    }
    if (formatedProvider === 'messenger') {
      identity.provider = 'microsoft';
    }
    return identity;
  });
};

const mapToInternalUser = (update, CAS_BPID) => {
  const { firstName, lastName, email, photoURL, country } = update.profile;

  const verifiedEmails = update.loginIDs?.emails
    ?.map((e) => e.toLowerCase())
    .filter((e) => e !== email?.toLowerCase());

  const unverifiedEmails = update.loginIDs?.unverifiedEmails.map((e) =>
    e.toLowerCase()
  );

  const hasPassword = update.hasPassword;
  const subscriptions = update.subscriptions;
  const { lastSelectedOrg = null, claims = {} } = update.data || {};
  const { orgs = [], platformAccess = [], roles = [] } = claims;

  const lastLogin = update.lastLoginTimestamp
    ? toDateString(update.lastLoginTimestamp)
    : '';

  const organisations = orgs.filter((it) => it.bpid && it.name);

  const currentOrganisation = lastSelectedOrg
    ? organisations.find((org) => org.bpid === lastSelectedOrg)
    : null;

  const organisationLegacyNumber = currentOrganisation?.sourceSystems?.find(
    (source) => source.systemId === 'LegacyCustomerNum'
  )?.orgId;

  const products = platformAccess
    .filter(
      ({ bpid }) =>
        (currentOrganisation && bpid === currentOrganisation.bpid) ||
        bpid === null
    )
    .map((el) => ({
      title: el.platformName,
      description: el.description || '',
      imageUrl: el.iconURL,
      url: el.platformURL,
    }));

  const socialProviders = mapSocialProviders(update.socialProviders);

  let formattedRoles = [];
  let isCambridgeAssessor = false;
  if (currentOrganisation !== null) {
    const orgRoles = roles.filter(
      (role) => role.bpid === currentOrganisation.bpid
    );
    formattedRoles = orgRoles[0]?.roleNames
      .map((name) => {
        return name.replace(new RegExp(/_/g), ' ');
      })
      .sort();
    isCambridgeAssessor = currentOrganisation.bpid === CAS_BPID;
  }

  const identities = mapIdentityProviders(update.identities);

  return {
    id: update.UID,
    fullName: `${firstName} ${lastName || ''}`.trim(),
    firstName: firstName,
    lastName: lastName,
    country: country,
    email: email,
    verifiedEmails: verifiedEmails,
    unverifiedEmails: unverifiedEmails,
    profilePicture: photoURL,
    hasPassword: hasPassword,
    organisationId: (currentOrganisation || {}).bpid,
    organisations: organisations,
    organisationName: (currentOrganisation || {}).name || '',
    organisationLegacyNumber,
    isCambridgeAssessor,
    subscriptions,
    products,
    lastLogin,
    socialProviders: socialProviders || [],
    roles: formattedRoles,
    identities: identities,
  };
};

export const UserProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(AuthStatus.Loading);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [isDelegate, setIsDelegate] = useState(false);

  const location = useLocation();
  const history = useHistory();

  const getCurrentOrg = (): CurrentOrganisation => {
    const bpid = user.organisationId;
    const organisation = user.organisations.find((org) => org.bpid === bpid);
    const legacyNumber = organisation.sourceSystems?.find(
      (source) => source.systemId === 'LegacyCustomerNum'
    )?.orgId;

    return { bpid, name: organisation.name, legacyNumber };
  };

  const isDelegateCallback = useCallback((accountInfo: AccountInfo) => {
    const lastSelectedOrg = accountInfo.data?.lastSelectedOrg;
    const isDA =
      lastSelectedOrg &&
      accountInfo.data?.claims?.roles
        ?.find((el) => el.bpid === lastSelectedOrg)
        .roleNames.includes(Roles.DELEGATED_ADMIN);
    setIsDelegate(isDA);
  }, []);

  const initiateLogin = useCallback(() => {
    (async () => {
      await waitForGigya();
      showLoginScreen(handleUserUpdate);
    })();
  }, []);

  const initiateLogout = useCallback(() => {
    setUser(null);
    clearCookies();
    clearStorage();
    logoutRedirect();
  }, []);

  const initiateUpdate = useCallback(() => {
    setLastUpdated(Date.now());
  }, [setLastUpdated]);

  const handleUserUpdate = useCallback(
    async (update) => {
      if (!update) return;
      const updateStatus = update.status || (update.response || {}).status;
      if (updateStatus === 'FAIL') {
        setStatus(AuthStatus.Unauthenticated);
        return;
      } else if (update.errorCode) {
        setStatus(AuthStatus.Error);
        return;
      }

      setStatus(AuthStatus.Authenticated);

      try {
        const result = await getUserDetails();
        const CAS_BPID = envVariables.CAS_BPID;
        if (result) {
          isDelegateCallback(result);

          setUser((prev) => ({
            ...(prev || {}),
            ...mapToInternalUser(result, CAS_BPID),
          }));
        }
        return;
      } catch (err) {
        return;
      }
    },
    [setUser]
  );

  useEffect(() => {
    (async () => {
      setStatus(AuthStatus.Loading);
      await waitForGigya();
      getAccountInfo(handleUserUpdate);
    })();
  }, [lastUpdated]);

  useEffect(() => {
    setInterval(() => {
      initiateUpdate();
    }, 30000);
  }, []);

  useEffect(() => {
    const params = decodeHashToParams(location.hash);

    if (location.hash) {
      history.replace(params.state);
    }
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        status,
        login: initiateLogin,
        logout: initiateLogout,
        update: initiateUpdate,
        isDelegate,
        getCurrentOrg,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default useAuth;
